import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { tap, takeUntil, take, map, catchError } from 'rxjs/operators';

import { SessionWarningModalComponent } from '@global/shared-features/modals/session-warning-modal/session-warning-modal.component';

import { SessionService } from '@global/services/session/session.service';
import { ModalService } from 'dlg-walrus';

import { MainActions, RenewActions } from '@ren/main/state/actions';

import {
  selectJourneyPath,
  selectNavigationDetails,
  selectPanelConfig,
} from '@ren/main/state/selectors/journey.selectors';
import {
  selectIsAutoRenewal,
  selectPolicyChangeDate,
  selectPolicyId,
  selectProgressbarDisplayStatus,
  selectShowRenewDetails,
} from '@ren/main/state/selectors/renewal.selectors';

import { ButtonState } from '@global/infrastructure/interfaces/button-state';
import { PanelState } from '@global/infrastructure/interfaces/panel-state';
import { environment } from '@ren-env/environment';
import { SESSION_TIMEOUT_URL } from '@ren/infrastructure/constants';
import { JourneyFeaturesConfig } from '@ren/infrastructure/configs/journey-features.config';

import { PostNavigationService } from '@global/services/post-navigation/post-navigation.service';
import { Utility } from '@global/infrastructure/helpers/utility';

@Component({
  selector: 'app-main-container',
  templateUrl: './main-container.component.html',
})
export class MainContainerComponent
  implements OnInit, AfterViewChecked, OnDestroy
{
  keepTracking$: Subject<boolean> = new Subject<boolean>();

  navigationDetails$: Observable<ButtonState[]>;
  panelConfig$: Observable<PanelState>;
  isShowProgressBar$: Observable<boolean>;
  isShowJourneyNav$: Observable<boolean>;
  policyId$: Observable<string>;
  policyChangeDate$: Observable<Date>;
  journeyPath$: Observable<any>;
  isReviewSelected$: Observable<boolean>;
  policyId: string;
  isInitialPage: boolean;
  isEditPage: boolean;

  brandName = environment.brand_copy.brand;
  cookieUrl = environment.brand_copy.cookieUrl;
  isAbTest5389$: Observable<boolean>;
  isAutoRenewal$: Observable<boolean>;

  constructor(
    private readonly store: Store,
    private readonly cd: ChangeDetectorRef,
    private readonly title: Title,
    private readonly router: Router,
    private readonly sessionService: SessionService,
    private readonly modalService: ModalService,
    private readonly postNavigationService: PostNavigationService,
    private readonly renderer: Renderer2
  ) {
    postNavigationService.renderer = this.renderer;
  }

  ngOnInit(): void {
    this.store.dispatch(
      RenewActions.showProgressBar({ isDisplayProgressBar: false })
    );
    this.navigationDetails$ = this.store.pipe(select(selectNavigationDetails));
    this.isReviewSelected$ = this.store.pipe(select(selectShowRenewDetails));
    this.panelConfig$ = this.store.pipe(
      select(selectPanelConfig),
      tap((config) => {
        if (config.pageTitle) {
          this.title.setTitle(config.pageTitle);
        }
      })
    );
    this.policyId$ = this.store.pipe(
      select(selectPolicyId),
      tap((policyId) => {
        this.policyId = policyId;
      })
    );
    this.policyChangeDate$ = this.store.pipe(select(selectPolicyChangeDate));
    this.journeyPath$ = this.store.pipe(select(selectJourneyPath));

    this.isShowProgressBar$ = this.store.pipe(
      select(selectProgressbarDisplayStatus)
    );

    this.postNavigationService
      .onNavigate()
      .pipe(takeUntil(this.keepTracking$))
      .subscribe((url) => this.setShowJourneyNav(url));

    this.sessionService.getSessionExpirySeconds$.subscribe((session) =>
      this.openSessionExpiryModal(session)
    );
  }

  ngAfterViewChecked(): void {
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.keepTracking$.next(false);
    this.keepTracking$.unsubscribe();
  }

  private openSessionExpiryModal(session) {
    if (
      this.router.url !==
      `${JourneyFeaturesConfig.errors.path}/${SESSION_TIMEOUT_URL}`
    ) {
      const expirySeconds = session.secondsRemaining;
      if (expirySeconds > 0) {
        this.modalService
          .open(SessionWarningModalComponent, { expirySeconds })
          .closed$.subscribe((result) => {
            if (result === 'exit') {
              this.store.dispatch(MainActions.redirectToMyAccountDashboard());
            } else if (result === 'timeout') {
              this.router.navigate([
                `${JourneyFeaturesConfig.errors.path}/${SESSION_TIMEOUT_URL}`,
              ]);
            } else {
              this.sessionService
                .refreshSession(session.sessionId)
                .pipe(
                  take(1),
                  catchError(() =>
                    this.router.navigate([
                      `${JourneyFeaturesConfig.errors.path}/${SESSION_TIMEOUT_URL}`,
                    ])
                  )
                )
                .subscribe(() => this.ngOnInit());
            }
          });
      } else {
        this.router.navigate([
          `${JourneyFeaturesConfig.errors.path}/${SESSION_TIMEOUT_URL}`,
        ]);
      }
    }
  }

  private setShowJourneyNav(url: string) {
    this.isAutoRenewal$ = this.store.pipe(select(selectIsAutoRenewal));
    this.isShowJourneyNav$ = this.store.pipe(
      take(1),
      select(selectIsAutoRenewal),
      map(
        (isAuto) =>
          isAuto || url.indexOf(JourneyFeaturesConfig.reviewInitial.path) === -1
      )
    );
    this.isInitialPage = window.location.href.includes(
      JourneyFeaturesConfig.reviewInitial.path
    );
    this.isEditPage = window.location.href.includes(
      JourneyFeaturesConfig.reviewEdit.path
    );
    this.isAbTest5389$ = Utility.isAbTest('ds1-5389');
  }
}
